import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  ArrowLeft,
  FileText,
  RefreshCw,
  Ban,
  AlertTriangle,
} from 'lucide-react'

type Status =
  | 'pending'
  | 'processing'
  | 'issued'
  | 'rejected'
  | 'error'
  | 'cancelled'
  | 'awaiting_issue'
  | 'awaiting_fiscal_setup'

interface Invoice {
  id: number
  invoice_number: string | null
  sale_number: number | null
  status: Status
  student_name: string | null
  package_name: string | null
  amount: number | null
  pdf_url: string | null
  error_message: string | null
  provider: string
  reissuable: boolean
  cancellable: boolean
  issued_at: string | null
  created_at: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface FiscalInvoicesProps {
  /** Sem conexao nao ha o que reemitir, cancelar ou sincronizar. */
  conta_azul_connected: boolean
  invoices: Invoice[]
  filters: { status?: string; start_date?: string; end_date?: string }
  statuses: Status[]
  pagination: Pagination
}

const STATUS_META: Record<
  Status,
  {
    label: string
    variant: 'default' | 'secondary' | 'destructive' | 'outline'
    className?: string
  }
> = {
  pending: { label: 'Pendente', variant: 'outline' },
  processing: { label: 'Processando', variant: 'secondary' },
  awaiting_issue: {
    label: 'Aguardando emissão',
    variant: 'secondary',
    className: 'text-amber-700',
  },
  issued: {
    label: 'Emitida',
    variant: 'secondary',
    className: 'text-emerald-700',
  },
  rejected: { label: 'Rejeitada', variant: 'destructive' },
  error: { label: 'Erro', variant: 'destructive' },
  cancelled: {
    label: 'Cancelada',
    variant: 'outline',
    className: 'text-muted-foreground',
  },
  awaiting_fiscal_setup: {
    label: 'Aguardando cadastro fiscal',
    variant: 'outline',
    className: 'text-amber-700',
  },
}

/**
 * Nunca deixa um status desconhecido derrubar a tela.
 *
 * A lista vem de FiscalInvoice.statuses.keys no servidor: adicionar um valor no
 * enum do Rails sem tocar aqui fazia STATUS_META[s] voltar undefined e o
 * `.label` estourar, quebrando a pagina inteira. Foi o que aconteceu quando
 * `awaiting_fiscal_setup` entrou.
 */
const statusMeta = (s: Status) =>
  STATUS_META[s] ?? { label: s, variant: 'outline' as const }

const formatCurrency = (value: number | null) =>
  value == null
    ? '—'
    : value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function FiscalInvoices({
  conta_azul_connected,
  invoices,
  filters,
  statuses,
  pagination,
}: FiscalInvoicesProps) {
  const [status, setStatus] = useState(filters.status || 'all')
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')
  const [pendingId, setPendingId] = useState<number | null>(null)
  const [cancelTarget, setCancelTarget] = useState<Invoice | null>(null)
  const [syncing, setSyncing] = useState(false)

  const handleSync = () => {
    setSyncing(true)
    router.post(
      '/settings/fiscal_invoices/sync',
      {},
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Sincronização concluída.'),
        onError: () =>
          toast.error('Não foi possível sincronizar com a Conta Azul.'),
        onFinish: () => setSyncing(false),
      },
    )
  }

  const applyFilters = (overrides: Record<string, string> = {}) => {
    const query: Record<string, string> = {
      ...(status !== 'all' ? { status } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      ...overrides,
    }
    router.get('/settings/fiscal_invoices', query, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const goToPage = (page: number) => applyFilters({ page: String(page) })

  const handleReissue = (invoice: Invoice) => {
    setPendingId(invoice.id)
    router.post(
      `/settings/fiscal_invoices/${invoice.id}/reissue`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Reemissão enviada para processamento.'),
        onError: () => toast.error('Não foi possível reemitir a nota.'),
        onFinish: () => setPendingId(null),
      },
    )
  }

  const handleCancel = (invoice: Invoice) => {
    setPendingId(invoice.id)
    router.post(
      `/settings/fiscal_invoices/${invoice.id}/cancel`,
      {},
      {
        preserveScroll: true,
        onSuccess: () =>
          toast.success('Solicitação de cancelamento processada.'),
        onError: () => toast.error('Não foi possível cancelar a nota.'),
        onFinish: () => {
          setPendingId(null)
          setCancelTarget(null)
        },
      },
    )
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.get('/settings')}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Configurações
          </Button>
        </div>

        {!conta_azul_connected && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              A Conta Azul não está conectada. Reemitir, cancelar e sincronizar
              ficam indisponíveis até você reconectar em Configurações → Conta
              Azul.
            </span>
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Notas Fiscais</h2>
            <p className="text-sm text-muted-foreground">
              Vendas enviadas à Conta Azul. A nota é emitida pelo contador no
              painel da Conta Azul; use "Sincronizar" para atualizar as que já
              foram emitidas.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing || !conta_azul_connected}
          >
            <RefreshCw
              className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`}
            />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-4 pt-6">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {statusMeta(s).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="start_date">De</Label>
              <Input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Até</Label>
              <Input
                id="end_date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <Button onClick={() => applyFilters()}>Filtrar</Button>
            <Button
              variant="ghost"
              onClick={() => {
                setStatus('all')
                setStartDate('')
                setEndDate('')
                router.get('/settings/fiscal_invoices')
              }}
            >
              Limpar
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {pagination.total_count}{' '}
              {pagination.total_count === 1 ? 'nota' : 'notas'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma nota encontrada para os filtros selecionados.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Pacote</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => {
                    const meta = statusMeta(inv.status)
                    const busy = pendingId === inv.id
                    return (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">
                          {inv.invoice_number ||
                            (inv.sale_number
                              ? `Venda #${inv.sale_number}`
                              : '—')}
                        </TableCell>
                        <TableCell>{inv.student_name || '—'}</TableCell>
                        <TableCell>{inv.package_name || '—'}</TableCell>
                        <TableCell>{formatCurrency(inv.amount)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={meta.variant}
                            className={meta.className}
                          >
                            {meta.label}
                          </Badge>
                          {inv.error_message && (
                            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                              {inv.error_message}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {inv.issued_at || inv.created_at}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            {inv.pdf_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a
                                  href={inv.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                                </a>
                              </Button>
                            )}
                            {inv.reissuable && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy || !conta_azul_connected}
                                onClick={() => handleReissue(inv)}
                              >
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />{' '}
                                Reemitir
                              </Button>
                            )}
                            {inv.cancellable && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                disabled={busy || !conta_azul_connected}
                                onClick={() => setCancelTarget(inv)}
                              >
                                <Ban className="h-3.5 w-3.5 mr-1" /> Retirar
                                venda
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Página {pagination.current_page} de {pagination.total_pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.current_page <= 1}
                    onClick={() => goToPage(pagination.current_page - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() => goToPage(pagination.current_page + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirar venda da Conta Azul?</AlertDialogTitle>
            <AlertDialogDescription>
              A venda{' '}
              {cancelTarget?.sale_number
                ? `nº ${cancelTarget.sale_number}`
                : ''}{' '}
              será removida na Conta Azul. Só é possível enquanto a NFS-e ainda
              não foi emitida — depois de emitida, o cancelamento é feito no
              painel da Conta Azul.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelTarget && handleCancel(cancelTarget)}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  )
}
